import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import {useAcceptCallMutation} from '../app/redux/api'
import {useAuth} from '../app/modules/auth/core/Auth'
import {useGetCurrentAgentStatusQuery} from '../app/redux/agentStatusApi'
import {setIncomingCallHandler, setIncomingCallMasterGuard} from '../services/firebaseMessaging'
import * as signalR from '@microsoft/signalr'
import {useAWSConnect} from '../app/context/ConnectStreamsContext'
import {streamsStore} from '../services/streamsStore'
import {callLogger} from '../Utils/ccpLogger'
import toaster from '../Utils/toaster'
import {triggerConnectDiagnosticsOnAccept} from '../services/connectEndpointTest'
import {
  clearInterpreterHubConnection,
  setInterpreterHubConnection,
} from '../services/agentNetworkTelemetry/interpreterHubConnection'
import {
  sendCallLivenessBeatNow,
  startCallLivenessHeartbeat,
} from '../services/callLivenessHeartbeat'
import {setAgentTelemetryRuntimeConfig} from '../services/agentNetworkTelemetry/runtimeConfig'
import {flushTelemetryOutbox} from '../services/agentNetworkTelemetry/telemetryOutbox'
import {
  createHandledCallState,
  findHandledCall,
  markCallHandled,
  markRemoteCallDismissed,
  normalizeCallHeaderId,
  removeHandledCall,
} from '../services/incomingCallHandledState'
import {
  CallGuardrailPatch,
  CallGuardrailRecord,
  getBlockingPreCallGuardrails,
  getCallGuardrail,
  getCallGuardrails,
  getPendingPreCallGuardrails,
  hasExhaustedSubmitAttempts,
  isBlockingGuardrail,
  MAX_PRECALL_SUBMIT_ATTEMPTS,
  recordPreCallSubmitFailure,
  getRestorableCallGuardrails,
  purgeCallGuardrailsExcept,
  removeCallGuardrail,
  updateCallGuardrail,
  upsertCallGuardrail,
} from '../services/preCallGuardrailStorage'
import {
  isSoftphoneRecoveryLocked,
  setSoftphoneRecoveryLocked,
} from '../services/softphoneRecoveryLock'

// Configuration
const MODAL_DISPLAY_TIME_MS = 20000 // 20 seconds
const LOCAL_STORAGE_QUEUE_KEY = 'incoming_call_queue'
// CallAcceptedByOther: how long the "Call Connected To Another User" popup stays visible (backend sends event only to losers)
const CALL_ACCEPTED_BY_OTHER_DISPLAY_MS = 10000
// Standby outcome: primary path is InterpreterHub SignalR `call:outcome` (same contract as
// GET /aws-connect/agents/call-outcome). That HTTP endpoint is kept as a future fallback if
// SignalR delivery cannot be relied on — do not delete it from api.ts.
// Safety backstop only. Prefer event-driven exits (REROUTED / TAKEN / WON / …).
// Sized above dynamic-queue nextRingAgent (~15s) so a genuine winner is never cut short.
const OUTCOME_DEADLINE_MS = 120000

// Same bases as SignalR's default policy, with jitter so tabs do not reconnect together.
const INTERPRETER_HUB_RETRY_DELAYS_MS = [0, 2000, 10000, 30000]

function nextInterpreterHubRetryDelay(retryContext: signalR.RetryContext): number | null {
  if (retryContext.previousRetryCount >= INTERPRETER_HUB_RETRY_DELAYS_MS.length) return null
  const base = INTERPRETER_HUB_RETRY_DELAYS_MS[retryContext.previousRetryCount]
  if (base === 0) return Math.round(Math.random() * 2000)
  const spread = base * 0.2
  return Math.max(0, Math.round(base + (Math.random() * 2 - 1) * spread))
}

// Interface for queued call in localStorage
interface QueuedCall {
  callId: string
  contactId: string // Used for removing calls with same contactId on reject
  callHeaderId: string
  caller: string
  language: string
  channel: string
  type: string
  timestamp: string
  action: string
  expiresAt: number // Timestamp when this call expires (20 seconds from when it was added)
}

interface CallData {
  callId: string
  action: string
  caller: string
  language: string
  channel: string
  type: string
  timestamp: string
  callHeaderId: string
  contactId?: string // Optional, used for removing by contactId
  preCallQA?: PreCallQAItem[]
  isClosed?: boolean // When true, call has disconnected and user can close the modal
  showPreCallQuestionsAlert?: boolean // When true, show "Pre-call questions must be completed to continue." in ActiveCallModal
}

interface IncomingCallContextType {
  showModal: boolean
  callData: CallData | null
  activeCalls: CallData[]
  callAcceptedByOther: boolean
  isAcceptedAndConnecting: boolean
  /** True while this interpreter has accepted and the outcome is not yet known. */
  isStandby: boolean
  /** True only for the loser terp (accepted but another got the call). Winner and idle never see it. */
  showCallAcceptedByOtherPopup: boolean
  showOfflineWarning: boolean
  /**
   * Latest device-token freshness pushed by the backend over InterpreterHub ("token:status").
   * false = the interpreter's notification token is missing/stale and they will miss calls.
   * null = unknown (no status received yet — treat as OK, do not prompt).
   */
  hasDeviceToken: boolean | null
  /**
   * True while the "Force Update Token" modal is actually on screen (owned by TokenUpdateModal,
   * which applies the OPI / on-call / grace-period gating). The Connect widget reads this to
   * minimize itself, force the agent offline, and disable its controls until the token is fixed.
   */
  tokenPromptActive: boolean
  setTokenPromptActive: (value: boolean) => void
  showIncomingCall: (data: CallData) => void
  hideIncomingCall: () => void
  acceptCall: (callId: string) => void
  rejectCall: (callId: string, isAutoRejected?: boolean) => void
  removeActiveCall: (callId: string) => void
  clearCallAcceptedByOther: () => void
  triggerOfflineWarning: () => void
  dismissOfflineWarning: () => void
  callGuardrails: CallGuardrailRecord[]
  hasPendingPreCallQuestions: boolean
  registerCallGuardrail: (data: CallData, patch?: CallGuardrailPatch) => void
  updateCallGuardrailState: (callHeaderId: string, patch: CallGuardrailPatch) => void
  /** Count a failed pre-call submission; releases the interpreter once attempts are exhausted. */
  registerPreCallSubmitFailure: (callHeaderId: string) => CallGuardrailRecord | undefined
  showPendingPreCallGuardrail: () => void
  /**
   * True while localStorage softphone recovery lock is set.
   * Armed when this agent accepted and the softphone connection failed.
   * CCP Available is restricted until an outbound softphone probe succeeds.
   */
  softphoneRecoveryLocked: boolean
  /** Recovery panel after the failed contact is closed and destroyed (and after reload). */
  showSoftphoneRecoveryPanel: boolean
  clearSoftphoneRecoveryLock: () => void
  /** Arm lock immediately (e.g. CCP detached after softphone failure). */
  armSoftphoneRecoveryLock: () => void
}

const initialIncomingCallState: IncomingCallContextType = {
  showModal: false,
  callData: null,
  activeCalls: [],
  callAcceptedByOther: false,
  isAcceptedAndConnecting: false,
  isStandby: false,
  showCallAcceptedByOtherPopup: false,
  showOfflineWarning: false,
  hasDeviceToken: null,
  tokenPromptActive: false,
  setTokenPromptActive: () => {},
  showIncomingCall: () => {},
  hideIncomingCall: () => {},
  acceptCall: () => {},
  rejectCall: () => {},
  removeActiveCall: () => {},
  clearCallAcceptedByOther: () => {},
  triggerOfflineWarning: () => {},
  dismissOfflineWarning: () => {},
  callGuardrails: [],
  hasPendingPreCallQuestions: false,
  registerCallGuardrail: () => {},
  updateCallGuardrailState: () => {},
  registerPreCallSubmitFailure: () => undefined,
  showPendingPreCallGuardrail: () => {},
  softphoneRecoveryLocked: false,
  showSoftphoneRecoveryPanel: false,
  clearSoftphoneRecoveryLock: () => {},
  armSoftphoneRecoveryLock: () => {},
}

// Types for the event payloads
interface CallDisconnectedPayload {
  contactId: string
  callHeaderId: number
  message: string
  action: 'DISCONNECTED'
  isClosed?: boolean
}

interface CallAcceptedPayload {
  contactId: string
  callHeaderId: number
  message: string
  action: 'ACCEPTED'
}

interface CallRoutedExternallyPayload {
  contactId: string
  callHeaderId: number
  message: string
  action: 'ROUTED_EXTERNALLY'
}

/** Same shape as GET /agents/call-outcome — pushed per interpreter on InterpreterHub. */
interface CallOutcomePayload {
  contactId: string
  callHeaderId: number
  state: 'PENDING' | 'WON' | 'TAKEN' | 'REROUTED' | 'ROUTED' | 'ENDED' | 'UNKNOWN'
  retryAfterMs: number
  message?: string
  action?: 'OUTCOME'
}

export interface PreCallQAItem {
  code: string
  question: string
  answer: string
}

interface CallQueuedPayload {
  contactId: string
  callHeaderId: number
  message: string
  action: 'QUEUED'
  caller?: string
  language?: string
  channel?: string
  type?: string
  timestamp?: string
  preCallQA?: PreCallQAItem[]
  showPreCallQuestionsAlert?: boolean
}

/** Same payload shape as call:queued; fired when agent softphone-connects (ActiveCallModal). */
interface CallConnectedPayload {
  contactId: string
  callHeaderId: number
  message: string
  action: 'CONNECTED'
  caller?: string
  language?: string
  channel?: string
  type?: string
  timestamp?: string
  preCallQA?: PreCallQAItem[]
  showPreCallQuestionsAlert?: boolean
}

// API response for GET /api/realtime/presence/active-calls (restore modals after refresh/login/browser change)
interface ActiveCallFromApi {
  contactId: string
  callId: number
  callerJson?: string | null
  requester?: string
  customer?: string
  languageTo?: string
  channel?: string
  communicationType?: string
  state?: string
  queueTimestamp?: string | null
}

const IncomingCallContext = createContext<IncomingCallContextType>(initialIncomingCallState)

// Helper functions for localStorage queue management
const getCallQueue = (): QueuedCall[] => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_QUEUE_KEY)
    if (!stored) return []
    const queue: QueuedCall[] = JSON.parse(stored)
    // Filter out expired calls
    const now = Date.now()
    const validQueue = queue.filter((call) => call.expiresAt > now)
    // Update storage if we filtered out expired calls
    if (validQueue.length !== queue.length) {
      setCallQueue(validQueue)
    }
    return validQueue
  } catch (error) {
    console.error('Error reading call queue from localStorage:', error)
    return []
  }
}

const setCallQueue = (queue: QueuedCall[]): void => {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(LOCAL_STORAGE_QUEUE_KEY)
    } else {
      localStorage.setItem(LOCAL_STORAGE_QUEUE_KEY, JSON.stringify(queue))
    }
  } catch (error) {
    console.error('Error writing call queue to localStorage:', error)
  }
}

const addCallToQueue = (call: CallData): void => {
  const queue = getCallQueue()
  // Check if call already exists
  const existingCallIndex = queue.findIndex(
    (c) =>
      c.callId === call.callId ||
      normalizeCallHeaderId(c.callHeaderId) === normalizeCallHeaderId(call.callHeaderId)
  )

  if (existingCallIndex !== -1) {
    // Call already exists - extend expiry time by 20 seconds
    const existingCall = queue[existingCallIndex]
    const newExpiresAt = Date.now() + MODAL_DISPLAY_TIME_MS
    queue[existingCallIndex] = {
      ...existingCall,
      expiresAt: newExpiresAt,
    }
    setCallQueue(queue)
    console.log(
      '📞 Call already in queue, extended expiry time:',
      call.callId,
      'New expiry:',
      new Date(newExpiresAt).toISOString()
    )
  } else {
    // New call - add to queue
    const queuedCall: QueuedCall = {
      ...call,
      contactId: call.contactId || call.callId, // Use contactId if provided, fallback to callId
      expiresAt: Date.now() + MODAL_DISPLAY_TIME_MS, // Expires in 20 seconds
    }
    queue.push(queuedCall)
    setCallQueue(queue)
    console.log('📞 Added call to queue:', queuedCall.callId, 'Queue size:', queue.length)
  }
}

const removeCallFromQueue = (callId: string, callHeaderId?: string): void => {
  const queue = getCallQueue()
  const filtered = queue.filter(
    (c) =>
      c.callId !== callId &&
      (callHeaderId == null ||
        normalizeCallHeaderId(c.callHeaderId) !== normalizeCallHeaderId(callHeaderId))
  )
  setCallQueue(filtered)
}

const removeCallsByContactId = (contactId: string): void => {
  const queue = getCallQueue()
  const filtered = queue.filter((c) => c.contactId !== contactId)
  setCallQueue(filtered)
}

const clearCallQueue = (): void => {
  setCallQueue([])
}

function syncStreamsIncomingCall(data: CallData) {
  streamsStore.syncIncomingCallMeta({
    callId: data.callId,
    contactId: data.contactId || data.callId,
    callHeaderId: data.callHeaderId,
    caller: data.caller,
    language: data.language,
    channel: data.channel,
    type: data.type,
    timestamp: data.timestamp,
    action: data.action,
  })
}

export const IncomingCallProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [showModal, setShowModal] = useState(false)
  const [callData, setCallData] = useState<CallData | null>(null)
  const [activeCalls, setActiveCalls] = useState<CallData[]>(() =>
    getRestorableCallGuardrails().map((record) => ({
      ...(record.callData as CallData),
      isClosed: record.isClosed,
    }))
  )
  const [callGuardrails, setCallGuardrails] = useState<CallGuardrailRecord[]>(getCallGuardrails)
  const [callAcceptedByOther, setCallAcceptedByOther] = useState(false)
  const [isAcceptedAndConnecting, setIsAcceptedAndConnecting] = useState(false)
  const [showOfflineWarning, setShowOfflineWarning] = useState(false)
  const [hasDeviceToken, setHasDeviceToken] = useState<boolean | null>(null)
  const [tokenPromptActive, setTokenPromptActive] = useState(false)
  const [softphoneRecoveryLocked, setSoftphoneRecoveryLockedState] = useState(
    () => isSoftphoneRecoveryLocked(),
  )
  const [showSoftphoneRecoveryPanel, setShowSoftphoneRecoveryPanel] = useState(
    () => isSoftphoneRecoveryLocked(),
  )
  const softphoneRecoveryLockedRef = useRef(softphoneRecoveryLocked)
  useEffect(() => {
    softphoneRecoveryLockedRef.current = softphoneRecoveryLocked
  }, [softphoneRecoveryLocked])

  const armSoftphoneRecoveryLock = useCallback(() => {
    setSoftphoneRecoveryLocked(true)
    setSoftphoneRecoveryLockedState(true)
    setShowSoftphoneRecoveryPanel(true)
  }, [])

  const clearSoftphoneRecoveryLock = useCallback(() => {
    setSoftphoneRecoveryLocked(false)
    setSoftphoneRecoveryLockedState(false)
    setShowSoftphoneRecoveryPanel(false)
  }, [])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'ccpSoftphoneRecoveryLock') return
      const locked = e.newValue === '1'
      setSoftphoneRecoveryLockedState(locked)
      if (locked) setShowSoftphoneRecoveryPanel(true)
      else setShowSoftphoneRecoveryPanel(false)
    }
    const onCustom = (e: Event) => {
      const locked = Boolean((e as CustomEvent)?.detail?.locked)
      setSoftphoneRecoveryLockedState(locked)
      if (locked) setShowSoftphoneRecoveryPanel(true)
      else setShowSoftphoneRecoveryPanel(false)
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('ccp:softphone-recovery-lock', onCustom as EventListener)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('ccp:softphone-recovery-lock', onCustom as EventListener)
    }
  }, [])

  const isAcceptedAndConnectingRef = useRef(false)
  const isConnectedRef = useRef(false)
  const callAcceptedByOtherTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const connectingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasClickedAcceptRef = useRef(false)

  // Standby: this interpreter accepted and the outcome is not yet known. The modal stays
  // mounted showing "Please Standby" until SignalR call:outcome (or local CCP connect /
  // deadline) resolves it.
  // Future: re-enable GET /aws-connect/agents/call-outcome poll as a SignalR fallback.
  const [standbyCall, setStandbyCall] = useState<{
    contactId: string
    callHeaderId: string
    startedAt: number
  } | null>(null)
  const standbyCallRef = useRef<{
    contactId: string
    callHeaderId: string
    startedAt: number
  } | null>(null)
  const isStandby = standbyCall !== null

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const modalTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isProcessingRef = useRef(false)
  const showModalRef = useRef(false)
  const callDataRef = useRef<CallData | null>(null)
  const processNextCallRef = useRef<(() => void) | null>(null)
  const lastAuthAvailabilitySkipLogAtRef = useRef<number>(0)
  // Track handled calls so duplicate FCM / queue replay cannot re-show after accept/reject/remote end.
  const handledCallIdsRef = useRef(createHandledCallState())
  const showIncomingCallRef = useRef<(data: CallData) => void>(() => {})
  // In-flight accept: callIds marked handled on click but not yet confirmed via streamsStore.contact:connected.
  // On contact:missed / autoAccept:expired, these get un-blocked so Connect can re-route to this agent.
  const pendingAcceptRef = useRef<{callId: string; callHeaderId: string} | null>(null)

  const {auth, currentUser} = useAuth()
  const [acceptCallMutation] = useAcceptCallMutation()

  useEffect(() => {
    standbyCallRef.current = standbyCall
  }, [standbyCall])

  // Check if agent is available
  const {data: agentStatusData} = useGetCurrentAgentStatusQuery(undefined, {
    skip:
      !auth?.isAuthenticated ||
      !currentUser ||
      currentUser.result?.userType === 'ADMIN' ||
      currentUser.result?.userType === 'REQUESTER' ||
      currentUser.result?.userType === 'CONSUMER' ||
      currentUser.result?.userType === 'SYSTEM',
  })

  // Get local Connect Streams status (and connection state for CallAcceptedByOther logic)
  const {
    setActiveCallData,
    agentStatus: localAgentStatus,
    isInitialized: isLocalConnectInitialized,
    isConnected,
    isIncomingCall,
    isMasterTab,
  } = useAWSConnect()
  const isIncomingCallMaster = isMasterTab === true

  const refreshCallGuardrails = useCallback(() => {
    setCallGuardrails(getCallGuardrails())
  }, [])

  const registerCallGuardrail = useCallback(
    (data: CallData, patch: CallGuardrailPatch = {}) => {
      upsertCallGuardrail(data, patch)
      refreshCallGuardrails()
    },
    [refreshCallGuardrails]
  )

  const updateCallGuardrailState = useCallback(
    (callHeaderId: string, patch: CallGuardrailPatch) => {
      updateCallGuardrail(callHeaderId, patch)
      refreshCallGuardrails()
    },
    [refreshCallGuardrails]
  )

  const registerPreCallSubmitFailure = useCallback(
    (callHeaderId: string) => {
      const record = recordPreCallSubmitFailure(callHeaderId)
      refreshCallGuardrails()
      if (record) {
        const exhausted = hasExhaustedSubmitAttempts(record)
        callLogger.warn('🔷 Pre-call submit failed', {
          callHeaderId,
          failedSubmitAttempts: record.failedSubmitAttempts,
          maxAttempts: MAX_PRECALL_SUBMIT_ATTEMPTS,
          releasedInterpreter: exhausted,
        })
      }
      return record
    },
    [refreshCallGuardrails]
  )

  const showPendingPreCallGuardrail = useCallback(() => {
    const pending = getPendingPreCallGuardrails()[0]
    if (!pending) return
    const pendingCall = {
      ...(pending.callData as CallData),
      isClosed: pending.isClosed,
      showPreCallQuestionsAlert: true,
    }
    setActiveCalls((prev) => {
      const exists = prev.some((call) => call.callHeaderId === pending.callHeaderId)
      return exists ? prev : [pendingCall, ...prev]
    })
    refreshCallGuardrails()
  }, [refreshCallGuardrails])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'pre_call_guardrails_v1') return
      refreshCallGuardrails()
      const restoredCalls = getRestorableCallGuardrails().map((record) => ({
        ...(record.callData as CallData),
        isClosed: record.isClosed,
      }))
      setActiveCalls(restoredCalls)
    }
    const handleShowRequired = () => {
      showPendingPreCallGuardrail()
    }
    window.addEventListener('storage', handleStorage)
    window.addEventListener('precall:show-required', handleShowRequired)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('precall:show-required', handleShowRequired)
    }
  }, [refreshCallGuardrails, showPendingPreCallGuardrail])

  // Previous behavior: Backend ROUTABLE OR local CCP Available/Routable.
  // ACW is CCP-authoritative. New rule only: once CCP is initialized, Offline CCP
  // must not show the modal even if backend presence still says ROUTABLE.
  const isAgentAvailable =
    localAgentStatus !== 'AfterCallWork' &&
    !(isLocalConnectInitialized && localAgentStatus === 'Offline') &&
    ((agentStatusData?.status === '200' && agentStatusData?.currentStatus?.type === 'ROUTABLE') ||
      (isLocalConnectInitialized &&
        (localAgentStatus === 'Available' || localAgentStatus === 'Routable')))

  // Clear isAcceptedAndConnecting once the call is fully connected
  useEffect(() => {
    if (isConnected && isAcceptedAndConnecting) {
      setIsAcceptedAndConnecting(false)
    }
  }, [isConnected, isAcceptedAndConnecting])

  // Bridge Connect Streams store events → queue/handled-state reconciliation.
  // Event-driven cleanup; OUTCOME_DEADLINE_MS is the only backstop while awaiting call:outcome.
  useEffect(() => {
    return streamsStore.onEvent((event) => {
      if (event.type === 'contact:connected') {
        // Winner: accept completed end-to-end. Lock in handled state, stop the safety timer.
        if (connectingTimeoutRef.current) {
          clearTimeout(connectingTimeoutRef.current)
          connectingTimeoutRef.current = null
        }
        pendingAcceptRef.current = null
        isAcceptedAndConnectingRef.current = false
        setIsAcceptedAndConnecting(false)
        return
      }

      if (event.type === 'contact:ended' || event.type === 'contact:destroyed') {
        // Show close/feedback as soon as CCP ends this contact. SignalR call:disconnected is a
        // backup (EventBridge → Kinesis → BE) and is often delayed or missing.
        // Exact contactId match only — never close "the only open modal" for a different
        // contact (that caused Feedback beside a newer QUEUED Active Call).
        const contactId = event.contactId
        // Prefer contactId match. Also allow callHeaderId from store meta while it still
        // points at this contact (available on contact:ended; cleared before destroy).
        const storeMeta = streamsStore.getSnapshot().activeCallData
        const storeHeaderId =
          storeMeta && (storeMeta.contactId === contactId || storeMeta.callId === contactId)
            ? String(storeMeta.callHeaderId)
            : null

        setActiveCalls((prev) => {
          let didClose = false
          const next = prev.map((call) => {
            const isMatch =
              call.callId === contactId ||
              call.contactId === contactId ||
              (storeHeaderId != null && call.callHeaderId === storeHeaderId)
            if (!isMatch || call.isClosed) return call
            didClose = true
            updateCallGuardrail(call.callHeaderId, {isClosed: true})
            return {...call, isClosed: true}
          })
          return didClose ? next : prev
        })
        setCallGuardrails(getCallGuardrails())
        return
      }

      if (event.type === 'contact:missed' || event.type === 'autoAccept:expired') {
        // Telephony-level failure after accept, or auto-accept window expired without any
        // contact arriving. Un-block the in-flight accepted IDs so Amazon Connect's next
        // routing attempt for the same contact can re-show the modal.
        const pending = pendingAcceptRef.current
        if (pending) {
          const ids = handledCallIdsRef.current
          removeHandledCall(ids, pending.callId, pending.callHeaderId)
          callLogger.warn('🔷 Un-blocked accepted call IDs after streams event', {
            event: event.type,
            callId: pending.callId,
            callHeaderId: pending.callHeaderId,
          })
          pendingAcceptRef.current = null
        }
        if (connectingTimeoutRef.current) {
          clearTimeout(connectingTimeoutRef.current)
          connectingTimeoutRef.current = null
        }
        isAcceptedAndConnectingRef.current = false
        setIsAcceptedAndConnecting(false)

        // Kick the queue so any waiting calls can be presented.
        setTimeout(() => processNextCallRef.current?.(), 200)
      }
    })
  }, [])

  /**
   * Leave standby. `outcome` decides whether the "Call Connected To Another User" panel
   * is shown. Called from SignalR call:outcome, local CCP connect, or the deadline timer.
   */
  const resolveStandby = useCallback(
    (outcome: 'WON' | 'TAKEN' | 'REROUTED' | 'ROUTED' | 'ENDED' | 'UNKNOWN' | 'TIMEOUT') => {
      const standby = standbyCallRef.current
      if (!standby) return
      standbyCallRef.current = null
      setStandbyCall(null)

      // Disarm auto-accept on every exit so losers/rerouted agents do not steal the next offer.
      streamsStore.cancelAutoAccept()

      // Close the modal in every case; the widget owns what happens next.
      isProcessingRef.current = false
      showModalRef.current = false
      setShowModal(false)
      setCallData(null)

      const showsLoserPanel =
        outcome === 'TAKEN' ||
        outcome === 'REROUTED' ||
        outcome === 'ROUTED' ||
        outcome === 'TIMEOUT'

      // Always logged, whatever the transport did — this is the reliability fix.
      callLogger.info('🔷 Accepted call outcome resolved', {
        contactId: standby.contactId,
        callHeaderId: standby.callHeaderId,
        outcome,
        waitedMs: Date.now() - standby.startedAt,
        shownLoserPanel: showsLoserPanel,
      })

      // Softphone already failed after accept — Test Connection stays up.
      // The other-user notice is for a reroute that did not fail the softphone.
      const recoveryAlreadyShowing =
        outcome === 'REROUTED' && isSoftphoneRecoveryLocked()

      if (showsLoserPanel && !recoveryAlreadyShowing) {
        setCallAcceptedByOther(true)
        if (callAcceptedByOtherTimeoutRef.current) {
          clearTimeout(callAcceptedByOtherTimeoutRef.current)
        }
        callAcceptedByOtherTimeoutRef.current = setTimeout(() => {
          setCallAcceptedByOther(false)
          callAcceptedByOtherTimeoutRef.current = null
          if (isSoftphoneRecoveryLocked()) {
            setShowSoftphoneRecoveryPanel(true)
          }
        }, CALL_ACCEPTED_BY_OTHER_DISPLAY_MS)
      }

      if (outcome !== 'WON') {
        isAcceptedAndConnectingRef.current = false
        setIsAcceptedAndConnecting(false)
      }
      pendingAcceptRef.current = null
      hasClickedAcceptRef.current = false
      setTimeout(() => processNextCallRef.current?.(), 200)
    },
    []
  )

  // Deadline-only backstop while waiting for SignalR call:outcome (or local CCP WON).
  // Future fallback: poll GET /aws-connect/agents/call-outcome (useLazyGetCallOutcomeQuery).
  useEffect(() => {
    if (!standbyCall) return

    // Local authority beats remote: if Connect already delivered the contact here, we won.
    if (isConnectedRef.current) {
      resolveStandby('WON')
      return
    }

    const remainingMs = OUTCOME_DEADLINE_MS - (Date.now() - standbyCall.startedAt)
    if (remainingMs <= 0) {
      resolveStandby('TIMEOUT')
      return
    }

    const timer = window.setTimeout(() => {
      if (isConnectedRef.current) {
        resolveStandby('WON')
        return
      }
      resolveStandby('TIMEOUT')
    }, remainingMs)

    return () => window.clearTimeout(timer)
  }, [standbyCall, resolveStandby])

  // Setup SignalR connection
  useEffect(() => {
    // Get your API base URL and access token
    const apiBaseUrl = process.env.REACT_APP_BASE_URL || 'https://your-api-url.com'
    const accessToken = auth?.token || ''

    // Create SignalR connection
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/interpreterHub`, {
        accessTokenFactory: () => accessToken || '',
        // If your token is passed as query parameter instead:
        // skipNegotiation: true,
        // transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: nextInterpreterHubRetryDelay,
      })
      .build()

    connectionRef.current = connection
    setInterpreterHubConnection(connection)
    startCallLivenessHeartbeat()

    // Mid Call Drop watchdog nudge: a live tab on a connected call answers with CallHeartbeat.
    connection.on('call:liveness', () => sendCallLivenessBeatNow())

    // Helper function to handle call removal from queue and modal closure
    const handleCallRemoval = (contactId: string, callHeaderId: number) => {
      const callHeaderIdStr = normalizeCallHeaderId(callHeaderId)

      // Terminal for this client: accepted elsewhere, disconnected, or routed externally.
      // Do not restore later FCM for the same contact — that caused post-accept modal ghosts.
      const handledCallIds = handledCallIdsRef.current
      markRemoteCallDismissed(handledCallIds, contactId, callHeaderIdStr)
      console.log('✅ Recorded terminal call dismissal via SignalR:', contactId, callHeaderIdStr)

      // If this interpreter is in standby for this call, keep Please Standby until
      // call:outcome (or CCP / deadline) resolves — do not tear the modal down here.
      const standby = standbyCallRef.current
      if (
        standby &&
        (standby.contactId === contactId ||
          normalizeCallHeaderId(standby.callHeaderId) === callHeaderIdStr)
      ) {
        removeCallsByContactId(contactId)
        removeCallFromQueue(contactId, callHeaderIdStr)
        return
      }

      // Check if the current modal is showing for this specific call
      const currentCallData = callDataRef.current
      const isCurrentCall =
        currentCallData &&
        (currentCallData.callId === contactId ||
          currentCallData.contactId === contactId ||
          normalizeCallHeaderId(currentCallData.callHeaderId) === callHeaderIdStr)

      if (isCurrentCall && showModalRef.current) {
        console.log('📞 Closing modal for call:', contactId, callHeaderIdStr)

        // Clear modal timeout
        if (modalTimeoutRef.current) {
          clearTimeout(modalTimeoutRef.current)
          modalTimeoutRef.current = null
        }

        // Close modal
        isProcessingRef.current = false
        showModalRef.current = false
        setShowModal(false)
        setCallData(null)
      }

      // Remove call from queue
      removeCallsByContactId(contactId)
      removeCallFromQueue(contactId, callHeaderIdStr)

      // Process next call after a short delay
      setTimeout(() => {
        if (processNextCallRef.current) {
          processNextCallRef.current()
        }
      }, 200)
    }

    // Listen to call:disconnected event - mark call as closed so ActiveCallModal shows close button
    connection.on('call:disconnected', (payload: CallDisconnectedPayload) => {
      console.log('📞 [SignalR] call:disconnected received:', JSON.stringify(payload))
      const contactId = payload.contactId
      const callHeaderIdStr = String(payload.callHeaderId)
      setActiveCalls((prev) =>
        prev.map((call) =>
          call.callId === contactId ||
          call.contactId === contactId ||
          normalizeCallHeaderId(call.callHeaderId) === callHeaderIdStr
            ? {...call, isClosed: payload.isClosed ?? true}
            : call
        )
      )
      updateCallGuardrail(callHeaderIdStr, {isClosed: true})
      refreshCallGuardrails()
      handleCallRemoval(payload.contactId, payload.callHeaderId)
    })

    // Call routed to operator or backstop — dismiss modal only; CCP popup shows on Accept API routedExternally rejection
    connection.on('call:routed_externally', (payload: CallRoutedExternallyPayload) => {
      console.log('📞 [SignalR] call:routed_externally received:', JSON.stringify(payload))
      handleCallRemoval(payload.contactId, payload.callHeaderId)
    })

    // Terminal for this contact on this client. handleCallRemoval keeps standby open until
    // call:outcome resolves; otherwise it only clears that contact from the queue/modal.
    connection.on('call:accepted', (payload: CallAcceptedPayload) => {
      console.log('📞 [SignalR] call:accepted received:', JSON.stringify(payload))
      handleCallRemoval(payload.contactId, payload.callHeaderId)
    })

    // Push alternative to GET /agents/call-outcome — resolves Please Standby.
    connection.on('call:outcome', (payload: CallOutcomePayload) => {
      console.log('📞 [SignalR] call:outcome received:', JSON.stringify(payload))
      const standby = standbyCallRef.current
      if (!standby) return

      const callHeaderIdStr = normalizeCallHeaderId(payload.callHeaderId)
      const matches =
        standby.contactId === payload.contactId ||
        normalizeCallHeaderId(standby.callHeaderId) === callHeaderIdStr
      if (!matches) return

      // Local CCP connect still wins if it already landed.
      if (isConnectedRef.current) {
        resolveStandby('WON')
        return
      }

      if (!payload.state || payload.state === 'PENDING') return
      resolveStandby(payload.state)
    })

    const openActiveCallFromPayload = (
      payload: CallQueuedPayload | CallConnectedPayload,
      source: 'call:queued' | 'call:connected'
    ) => {
      console.log(`📞 ${source} via SignalR:`, payload)
      const contactId = payload.contactId
      const callHeaderIdStr = String(payload.callHeaderId)

      const queue = getCallQueue()
      const queuedMatch = queue.find(
        (c) =>
          c.contactId === contactId || c.callId === contactId || c.callHeaderId === callHeaderIdStr
      )

      const callDataForActive: CallData = {
        callId: contactId,
        contactId: contactId,
        callHeaderId: callHeaderIdStr,
        caller: payload.caller ?? queuedMatch?.caller ?? '',
        language: payload.language ?? queuedMatch?.language ?? '',
        channel: payload.channel ?? queuedMatch?.channel ?? '',
        type: payload.type ?? queuedMatch?.type ?? '',
        timestamp: payload.timestamp ?? queuedMatch?.timestamp ?? new Date().toISOString(),
        action: payload.action,
        preCallQA: Array.isArray(payload.preCallQA) ? payload.preCallQA : undefined,
        isClosed: false,
        showPreCallQuestionsAlert: payload.showPreCallQuestionsAlert === true,
      }
      // A new active call supersedes any prior modal (interpreter cannot take another call
      // until pre-call is complete). Drop stale guardrails before upserting the current call.
      purgeCallGuardrailsExcept(callHeaderIdStr)

      const hasPreCallQuestions = Boolean(callDataForActive.preCallQA?.length)
      const preCallSubmitted =
        hasPreCallQuestions &&
        callDataForActive.preCallQA!.every((item) => Boolean(item.answer?.trim()))
      // Upsert preserves completed preCallSubmitted / answered drafts in storage.
      upsertCallGuardrail(callDataForActive, {
        qaDraft: callDataForActive.preCallQA,
        hasPreCallQuestions,
        ...(preCallSubmitted ? {preCallSubmitted: true} : {}),
      })
      refreshCallGuardrails()

      setActiveCalls((prev) => {
        const existing = prev.find(
          (call) =>
            call.callHeaderId === callDataForActive.callHeaderId ||
            call.callId === callDataForActive.callId ||
            call.contactId === callDataForActive.contactId
        )
        if (!existing) return [callDataForActive]
        return [
          {
            ...existing,
            ...callDataForActive,
            // Keep closed state if SignalR call:disconnected already ended this call.
            isClosed: Boolean(existing.isClosed) || Boolean(callDataForActive.isClosed),
            preCallQA: callDataForActive.preCallQA ?? existing.preCallQA,
          },
        ]
      })

      // Set active call data immediately so widget gets the data
      try {
        const parsedCaller = callDataForActive.caller ? JSON.parse(callDataForActive.caller) : {}
        const ctxCallData = {
          callId: callDataForActive.callId,
          contactId: callDataForActive.contactId || callDataForActive.callId,
          callHeaderId: callDataForActive.callHeaderId,
          caller: parsedCaller,
          language: callDataForActive.language,
          channel: callDataForActive.channel,
          type: callDataForActive.type,
          timestamp: callDataForActive.timestamp,
          action: callDataForActive.action,
        }
        setActiveCallData(ctxCallData)
        streamsStore.syncIncomingCallMeta({
          ...ctxCallData,
          caller: callDataForActive.caller,
        })
      } catch (e) {
        console.error(`Failed to parse caller data for context in ${source}:`, e)
      }
    }

    // call:queued no longer opens ActiveCallModal — that waits for softphone connect.
    connection.on('call:queued', (payload: CallQueuedPayload) => {
      console.log('📞 Call queued via SignalR (ringing only; ActiveCallModal deferred):', payload)
    })

    connection.on('call:connected', (payload: CallConnectedPayload) => {
      openActiveCallFromPayload(payload, 'call:connected')
    })

    connection.on('ping', async () => {
      try {
        if (connection.state === signalR.HubConnectionState.Connected) {
          await connection.invoke('Pong')
        }
      } catch (error) {
        console.error('Error responding to liveness ping:', error)
      }
    })

    // Server-measured backend RTT. send does not wait for a reply; a missed ack is an Influx gap.
    connection.on('telemetry:ping', (token: string) => {
      if (!token || connection.state !== signalR.HubConnectionState.Connected) return
      void connection.send('TelemetryPong', token).catch(() => {
        // A missed ack leaves an Influx gap. Do not retry.
      })
    })

    connection.on(
      'telemetry:config',
      (payload: {enabled?: boolean; idleIntervalMs?: number; activeIntervalMs?: number}) => {
        setAgentTelemetryRuntimeConfig(payload ?? {})
      }
    )

    // Backend pushes the interpreter's notification-token freshness. false => their token is
    // missing/stale and they will silently miss calls; the app shows a "Force Update Token" prompt.
    connection.on('token:status', (payload: {hasDeviceToken?: boolean}) => {
      setHasDeviceToken(Boolean(payload?.hasDeviceToken))
    })

    // Start the connection
    connection
      .start()
      .then(() => {
        console.log('SignalR Connected to InterpreterHub')
      })
      .catch((error) => {
        console.error('SignalR Connection Error:', error)
      })

    // Handle reconnection events
    connection.onreconnecting((error) => {
      console.log('SignalR Reconnecting...', error)
    })

    connection.onreconnected((connectionId) => {
      console.log('SignalR Reconnected. Connection ID:', connectionId)
      void flushTelemetryOutbox()
      sendCallLivenessBeatNow()
    })

    connection.onclose((error) => {
      console.log('SignalR Connection Closed', error)
    })

    // Cleanup on unmount
    return () => {
      if (connectionRef.current) {
        connectionRef.current.off('call:disconnected')
        connectionRef.current.off('call:accepted')
        connectionRef.current.off('call:routed_externally')
        connectionRef.current.off('call:outcome')
        connectionRef.current.off('call:queued')
        connectionRef.current.off('call:connected')
        connectionRef.current.off('ping')
        connectionRef.current.off('call:liveness')
        connectionRef.current.off('telemetry:ping')
        connectionRef.current.off('telemetry:config')
        connectionRef.current.off('token:status')
        clearInterpreterHubConnection(connectionRef.current)
        connectionRef.current.stop()
      }
    }
  }, [auth?.token, refreshCallGuardrails, resolveStandby])

  // Restore active call modals after page refresh, logout/login, or browser change (backend is source of truth for non-disconnected calls)
  useEffect(() => {
    if (!auth?.token) return
    const apiBaseUrl = process.env.REACT_APP_BASE_URL || ''
    if (!apiBaseUrl) return
    let cancelled = false
    const fetchActiveCalls = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/realtime/presence/active-calls`, {
          headers: {Authorization: `Bearer ${auth.token}`},
        })
        if (!res.ok || cancelled) return
        const list: ActiveCallFromApi[] = await res.json()
        const mapped: CallData[] = (list || []).map((item) => {
          const callHeaderIdStr = item.callId != null ? String(item.callId) : ''
          const caller =
            item.callerJson ||
            (item.requester != null || item.customer != null
              ? JSON.stringify({
                  firstName: item.requester ?? '',
                  lastName: '',
                  companyName: item.customer ?? 'N/A',
                })
              : '')
          return {
            callId: item.contactId,
            contactId: item.contactId,
            callHeaderId: callHeaderIdStr,
            caller,
            language: item.languageTo ?? '',
            channel: item.channel ?? item.communicationType ?? '',
            type: 'on_demand_call',
            timestamp: item.queueTimestamp
              ? new Date(item.queueTimestamp).toISOString()
              : new Date().toISOString(),
            action: item.state ?? 'QUEUED',
            isClosed: false,
          }
        })
        if (!cancelled) {
          mapped.forEach((call) => upsertCallGuardrail(call))
          const restored = getRestorableCallGuardrails().map((record) => ({
            ...(record.callData as CallData),
            isClosed: record.isClosed,
          }))
          setActiveCalls((prev) => {
            const next: CallData[] = []
            // Prefer in-memory / guardrail closed state over API rows that may still look live.
            ;[...prev, ...restored, ...mapped].forEach((call) => {
              const existingIndex = next.findIndex(
                (item) => item.callHeaderId === call.callHeaderId
              )
              if (existingIndex < 0) {
                next.push(call)
                return
              }
              const existing = next[existingIndex]
              next[existingIndex] = {
                ...existing,
                ...call,
                isClosed: Boolean(existing.isClosed) || Boolean(call.isClosed),
                preCallQA: call.preCallQA ?? existing.preCallQA,
              }
            })
            return next
          })
          refreshCallGuardrails()
        }
      } catch (e) {
        if (!cancelled) console.warn('Failed to restore active calls:', e)
      }
    }
    fetchActiveCalls()
    return () => {
      cancelled = true
    }
  }, [auth?.token, refreshCallGuardrails])

  // Keep showModalRef and callDataRef in sync with state
  useEffect(() => {
    showModalRef.current = showModal
  }, [showModal])

  useEffect(() => {
    callDataRef.current = callData
  }, [callData])

  // --- CallAcceptedByOther ---
  // Winner = accepted and CCP delivered the contact here. Loser = accepted, someone else
  // got it. Idle = never accepted. Only an interpreter who accepted ever enters standby,
  // and only standby can resolve to the panel, so "idle never sees it" is structural.
  //
  // A local CCP connect is the strongest signal we have and beats anything the backend
  // says, so it resolves standby as WON immediately.
  useEffect(() => {
    isConnectedRef.current = isConnected
    if (isConnected) {
      setCallAcceptedByOther(false)
      isAcceptedAndConnectingRef.current = false
      setIsAcceptedAndConnecting(false)
      if (standbyCallRef.current) {
        resolveStandby('WON')
      }
      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current)
        connectingTimeoutRef.current = null
      }
      if (callAcceptedByOtherTimeoutRef.current) {
        clearTimeout(callAcceptedByOtherTimeoutRef.current)
        callAcceptedByOtherTimeoutRef.current = null
      }
    }
  }, [isConnected, resolveStandby])

  const showCallAcceptedByOtherPopup = useMemo(
    () => Boolean(callAcceptedByOther && !isConnected && !isStandby),
    [callAcceptedByOther, isConnected, isStandby]
  )

  // Process next call from queue
  const processNextCall = useCallback(() => {
    console.log('🔄 processNextCall called', {
      isProcessing: isProcessingRef.current,
      showModal: showModalRef.current,
      showModalState: showModal,
      queueSize: getCallQueue().length,
    })

    if (isProcessingRef.current || showModalRef.current) {
      console.log('⏭️ Skipping - already processing or modal showing')
      return // Already processing or modal is showing
    }

    if (!isIncomingCallMaster) {
      console.log('⏭️ Skipping - not the Connect master tab')
      return
    }

    // Same guard as showIncomingCall: never present a queued call while pre-call questions
    // from a finished call are still outstanding. Covers calls already sitting in the queue
    // when the interpreter was forced offline.
    const blockingGuardrails = getBlockingPreCallGuardrails()
    if (blockingGuardrails.length > 0) {
      console.log('⏭️ Skipping - pre-call questions outstanding')
      return
    }

    if (!auth?.isAuthenticated || !currentUser || !isAgentAvailable) {
      console.log('⏭️ Skipping - user not authenticated or agent not available')
      const now = Date.now()
      // Avoid spamming logs because processNextCall can run frequently (storage events, timers, etc.)
      if (now - lastAuthAvailabilitySkipLogAtRef.current > 30000) {
        lastAuthAvailabilitySkipLogAtRef.current = now
        callLogger.warn(
          '🔷 Incoming call skipped — user not authenticated or agent not available',
          {
            isAuthenticated: Boolean(auth?.isAuthenticated),
            hasCurrentUser: Boolean(currentUser),
            isAgentAvailable: Boolean(isAgentAvailable),
            queueSize: getCallQueue().length,
          }
        )
      }
      return // User not authenticated or agent not available
    }

    if (isConnected || isAcceptedAndConnecting) {
      console.log('⏭️ Skipping - agent is already on a connected/connecting call')
      return // Agent is already on a connected or accepted-and-connecting call
    }

    const queue = getCallQueue()
    console.log(
      '📋 Current queue:',
      queue.map((c) => ({callId: c.callId, callHeaderId: c.callHeaderId}))
    )

    if (queue.length === 0) {
      console.log('📋 Queue is empty')
      return
    }

    // Get first non-expired call (FIFO - first in, first out)
    const now = Date.now()
    // Filter out expired calls and already handled calls
    const handledCallIds = handledCallIdsRef.current
    const validCalls = queue.filter((call) => {
      if (call.expiresAt <= now) return false // Expired
      // Check if already handled
      if (findHandledCall(handledCallIds, call.callId, call.callHeaderId)) {
        console.log(
          '⏭️ Skipping call in queue - already accepted/rejected:',
          call.callId,
          call.callHeaderId
        )
        return false
      }
      return true
    })

    if (validCalls.length === 0) {
      // All calls expired or handled, clear queue
      console.log('⏭️ All calls expired or handled, clearing queue')
      clearCallQueue()
      return
    }

    // Get the first call (FIFO order)
    const validCall = validCalls[0]

    console.log('✅ Processing call from queue:', validCall.callId, validCall.callHeaderId)
    console.log('📋 Queue before removal:', queue.length, 'calls')

    // Remove from queue and display
    removeCallFromQueue(validCall.callId, validCall.callHeaderId)

    // Verify removal
    const queueAfterRemoval = getCallQueue()
    console.log('📋 Queue after removal:', queueAfterRemoval.length, 'calls remaining')

    // Display the call
    hasClickedAcceptRef.current = false
    isProcessingRef.current = true
    showModalRef.current = true
    syncStreamsIncomingCall(validCall)
    setCallData(validCall)
    setShowModal(true)

    // Set timeout to auto-dismiss after 20 seconds
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current)
    }

    modalTimeoutRef.current = setTimeout(() => {
      console.log('📞 Modal timeout expired for call:', validCall.callId)
      hasClickedAcceptRef.current = false
      isProcessingRef.current = false
      showModalRef.current = false
      setShowModal(false)
      setCallData(null)
      // Process next call after a short delay to ensure state has updated
      setTimeout(() => {
        if (processNextCallRef.current) {
          processNextCallRef.current()
        }
      }, 200)
    }, MODAL_DISPLAY_TIME_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth,
    currentUser,
    isAgentAvailable,
    isConnected,
    isAcceptedAndConnecting,
    isIncomingCallMaster,
  ])

  // Store processNextCall in ref
  useEffect(() => {
    processNextCallRef.current = processNextCall
  }, [processNextCall])

  // Listen to service worker messages and localStorage changes (master tab only)
  useEffect(() => {
    if (!isIncomingCallMaster) return

    // Handle messages from service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'INCOMING_CALL_QUEUE' && event.data.callData) {
        console.log(
          '📞 Received call from service worker, validating:',
          event.data.callData.callId,
          event.data.callData.callHeaderId
        )
        const callData = event.data.callData
        showIncomingCallRef.current(callData)
      }
    }

    // Handle localStorage changes (for cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LOCAL_STORAGE_QUEUE_KEY) {
        console.log('📞 localStorage queue changed, processing...')
        // Always try to process (processNextCall will check if modal is showing)
        setTimeout(() => {
          if (processNextCallRef.current) {
            processNextCallRef.current()
          }
        }, 100)
      }
    }

    // Set up service worker message listener
    if ('serviceWorker' in navigator) {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
      }
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
        }
      })
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
      })
    }

    window.addEventListener('storage', handleStorageChange)

    // Also poll localStorage periodically (in case storage event doesn't fire)
    const pollInterval = setInterval(() => {
      const queue = getCallQueue()
      if (queue.length > 0 && !showModalRef.current && !isProcessingRef.current) {
        console.log('📋 Polling found calls in queue, processing...')
        if (processNextCallRef.current) {
          processNextCallRef.current()
        }
      }
    }, 1000) // Check every second

    // Initial check
    if (!showModalRef.current && !isProcessingRef.current) {
      setTimeout(() => {
        if (processNextCallRef.current) {
          processNextCallRef.current()
        }
      }, 500)
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(pollInterval)
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
    }
  }, [isIncomingCallMaster])

  // Handle accept call
  const acceptCall = useCallback(
    async (callId: string) => {
      if (!callData) {
        console.error('❌ No call data available to accept')
        callLogger.error('🔷 Accept failed — no call data available', {callId})
        return
      }

      // Mark this call as handled immediately (before API call) to prevent showing it again
      hasClickedAcceptRef.current = true
      isAcceptedAndConnectingRef.current = true
      setIsAcceptedAndConnecting(true)
      // Keep isAcceptedAndConnecting true until call:outcome (or CCP / deadline) resolves.
      // The old ACCEPTED_CONNECTING_SAFETY_MS timer is gone — OUTCOME_DEADLINE_MS is the
      // single backstop now, and it never fires before the winner could have connected.
      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current)
        connectingTimeoutRef.current = null
      }
      const acceptedCallId = callData.callId
      const acceptedCallHeaderId = callData.callHeaderId
      const handledCallIds = handledCallIdsRef.current
      markCallHandled(handledCallIds, callData.callId, callData.callHeaderId, 'local-accept')
      pendingAcceptRef.current = {callId: acceptedCallId, callHeaderId: acceptedCallHeaderId}
      console.log('✅ Marked call as accepted:', callData.callId, callData.callHeaderId)

      // Carry the accept intent into the Streams layer. This is what answers the CCP contact
      // when Connect finally delivers it — and re-answers it on every re-offer after a
      // softphone failure, without the interpreter having to click again.
      streamsStore.armAutoAccept({callHeaderId: acceptedCallHeaderId})

      // Block incoming call modals while CCP is connecting the accepted call
      setIsAcceptedAndConnecting(true)

      // Remove from queue immediately
      removeCallsByContactId(callData.contactId || callData.callId)

      // Clear modal timeout
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current)
        modalTimeoutRef.current = null
      }

      let enteredStandby = false

      try {
        console.log('📞 Accepting call:', callId)
        const result = await acceptCallMutation({
          contactId: callId,
          action: true,
        }).unwrap()

        if (result.success) {
          console.log('✅ Call accepted successfully')
          // Clear entire queue on successful accept
          clearCallQueue()
          // Fire-and-forget Connect diagnostics (client config + latency + media STUN).
          // Must not block CCP answer / standby flow.
          triggerConnectDiagnosticsOnAccept({
            callId: acceptedCallId,
            callHeaderId: acceptedCallHeaderId,
            contactId: callData.contactId || callData.callId,
            userId: currentUser?.result?.userId || currentUser?.result?.code,
            userEmail: currentUser?.result?.email,
            agentName: [currentUser?.result?.firstName, currentUser?.result?.lastName]
              .filter(Boolean)
              .join(' ')
              .trim(),
          })
          // Hold the modal on screen in its "Please Standby" face. The poll loop owns the
          // teardown from here — whether we won, lost, or the call went elsewhere.
          enteredStandby = true
        } else if (result.routedExternally) {
          console.log('⚠️ Call already routed externally — showing unavailable popup')
          standbyCallRef.current = {
            contactId: callData.contactId || callData.callId,
            callHeaderId: callData.callHeaderId,
            startedAt: Date.now(),
          }
          resolveStandby('ROUTED')
        } else {
          console.error('❌ Failed to accept call:', result)
          callLogger.error('🔷 Accept API failed (success=false)', {
            callId: callData.callId,
            contactId: callData.contactId ?? callData.callId,
            callHeaderId: callData.callHeaderId,
            language: callData.language,
            channel: callData.channel,
            type: callData.type,
            timestamp: callData.timestamp,
            result,
          })
          // Note: We keep the call marked as handled even if API fails
          // to prevent showing it again if a new notification arrives
        }
      } catch (error) {
        const status =
          error && typeof error === 'object' && 'status' in error
            ? Number((error as {status?: number | string}).status)
            : undefined

        if (status === 410) {
          console.log('⚠️ Accept rejected — notification expired (410 Gone / backstop transfer)')
          toaster('warning', 'The notification has expired')
          streamsStore.cancelAutoAccept()
          isAcceptedAndConnectingRef.current = false
          setIsAcceptedAndConnecting(false)
          pendingAcceptRef.current = null
          hasClickedAcceptRef.current = false
          callLogger.info('🔷 Accept API returned 410 — notification expired', {
            callId: callData.callId,
            contactId: callData.contactId ?? callData.callId,
            callHeaderId: callData.callHeaderId,
          })
        } else {
          console.error('❌ Error accepting call:', error)
          callLogger.error('🔷 Accept API threw exception', {
            callId: callData.callId,
            contactId: callData.contactId ?? callData.callId,
            callHeaderId: callData.callHeaderId,
            language: callData.language,
            channel: callData.channel,
            type: callData.type,
            timestamp: callData.timestamp,
            error: String(error),
          })
        }
      } finally {
        if (enteredStandby) {
          // Modal stays mounted, switching to the "Please Standby" face.
          setStandbyCall({
            contactId: callData.contactId || callData.callId,
            callHeaderId: callData.callHeaderId,
            startedAt: Date.now(),
          })
        } else {
          // Accept failed outright: close the modal as before.
          isProcessingRef.current = false
          showModalRef.current = false
          setShowModal(false)
          setCallData(null)
          setTimeout(() => {
            if (processNextCallRef.current) {
              processNextCallRef.current()
            }
          }, 200)
        }
      }
    },
    [callData, acceptCallMutation, resolveStandby, currentUser]
  )

  // Handle reject call
  const rejectCall = useCallback(
    async (callId: string, isAutoRejected: boolean = false) => {
      if (!callData) {
        console.error('❌ No call data available to reject')
        callLogger.error('🔷 Reject failed — no call data available', {callId, isAutoRejected})
        return
      }

      // Mark this call as handled immediately (before API call) to prevent showing it again
      hasClickedAcceptRef.current = false
      isAcceptedAndConnectingRef.current = false
      setIsAcceptedAndConnecting(false)
      pendingAcceptRef.current = null
      streamsStore.cancelAutoAccept()
      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current)
        connectingTimeoutRef.current = null
      }
      const handledCallIds = handledCallIdsRef.current
      markCallHandled(handledCallIds, callData.callId, callData.callHeaderId, 'local-reject')
      console.log('✅ Marked call as rejected:', callData.callId, callData.callHeaderId)

      // Clear modal timeout
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current)
        modalTimeoutRef.current = null
      }

      // Remove all calls with same contactId from queue
      const contactId = callData.contactId || callData.callId
      removeCallsByContactId(contactId)

      // Only call API if manually rejected (not auto-rejected)
      if (!isAutoRejected) {
        try {
          console.log('📞 Rejecting call:', callId)
          const result = await acceptCallMutation({
            contactId: callId,
            action: false,
          }).unwrap()

          if (result.success) {
            console.log('✅ Call rejected successfully')
          } else {
            console.error('❌ Failed to reject call:', result)
            callLogger.error('🔷 Reject API failed (success=false)', {
              callId: callData.callId,
              contactId: callData.contactId ?? callData.callId,
              callHeaderId: callData.callHeaderId,
              language: callData.language,
              channel: callData.channel,
              type: callData.type,
              timestamp: callData.timestamp,
              result,
            })
            // Note: We keep the call marked as handled even if API fails
            // to prevent showing it again if a new notification arrives
          }
        } catch (error) {
          console.error('❌ Error rejecting call:', error)
          callLogger.error('🔷 Reject API threw exception', {
            callId: callData.callId,
            contactId: callData.contactId ?? callData.callId,
            callHeaderId: callData.callHeaderId,
            language: callData.language,
            channel: callData.channel,
            type: callData.type,
            timestamp: callData.timestamp,
            error: String(error),
          })
          // Note: We keep the call marked as handled even if API fails
          // to prevent showing it again if a new notification arrives
        }
      }

      // Close modal
      isProcessingRef.current = false
      showModalRef.current = false
      setShowModal(false)
      setCallData(null)

      // Process next call after a short delay to ensure state has updated
      setTimeout(() => {
        if (processNextCallRef.current) {
          processNextCallRef.current()
        }
      }, 200)
    },
    [callData, acceptCallMutation]
  )

  const hideIncomingCall = useCallback(() => {
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current)
      modalTimeoutRef.current = null
    }
    hasClickedAcceptRef.current = false
    isAcceptedAndConnectingRef.current = false
    setIsAcceptedAndConnecting(false)
    if (connectingTimeoutRef.current) {
      clearTimeout(connectingTimeoutRef.current)
      connectingTimeoutRef.current = null
    }
    isProcessingRef.current = false
    showModalRef.current = false
    setShowModal(false)
    setCallData(null)
    // Process next call after a short delay
    setTimeout(() => {
      if (processNextCallRef.current) {
        processNextCallRef.current()
      }
    }, 200)
  }, [])

  const showIncomingCall = useCallback(
    (data: CallData) => {
      console.log('📞 showIncomingCall called:', data.callId, data.callHeaderId)

      if (!isIncomingCallMaster) {
        console.log('⏭️ Ignoring incoming call — not the Connect master tab')
        return
      }

      // Softphone recovery lock: suppress IncomingCallModal until outbound probe unlocks.
      if (softphoneRecoveryLockedRef.current || isSoftphoneRecoveryLocked()) {
        console.log('⏭️ Ignoring incoming call — softphone recovery lock active')
        callLogger.warn('🔷 Incoming call ignored — softphone recovery lock', {
          callId: data.callId,
          callHeaderId: data.callHeaderId,
        })
        return
      }

      // Unanswered pre-call questions force the interpreter offline. Read the guardrail store
      // directly instead of trusting isAgentAvailable: that flag is derived from two
      // eventually-consistent sources (the backend status query and the CCP agent state), and
      // neither reflects the force-offline until it round-trips — so a call arriving in that
      // window slipped through. This is synchronous and survives a reload.
      const blocking = getBlockingPreCallGuardrails()
      if (blocking.length > 0) {
        console.log('❌ Pre-call questions outstanding — ignoring incoming call')
        callLogger.warn('🔷 Incoming call ignored — pre-call questions outstanding', {
          callId: data.callId,
          callHeaderId: data.callHeaderId,
          blockingCallHeaderIds: blocking.map((r) => r.callHeaderId),
        })
        return
      }

      // Check if user is authenticated and agent is available
      if (!auth?.isAuthenticated || !currentUser || !isAgentAvailable) {
        console.log('❌ User not authenticated or agent not available, ignoring call')
        callLogger.warn(
          '🔷 Incoming call ignored — user not authenticated or agent not available',
          {
            callId: data.callId,
            callHeaderId: data.callHeaderId,
            language: data.language,
            channel: data.channel,
            type: data.type,
            timestamp: data.timestamp,
            isAuthenticated: Boolean(auth?.isAuthenticated),
            hasCurrentUser: Boolean(currentUser),
            isAgentAvailable: Boolean(isAgentAvailable),
          }
        )
        return
      }

      const handledCall = findHandledCall(handledCallIdsRef.current, data.callId, data.callHeaderId)
      if (handledCall) {
        callLogger.warn('🔷 Incoming call dropped — already handled', {
          callId: data.callId,
          callHeaderId: data.callHeaderId,
          handledSource: handledCall.source,
          handledAt: new Date(handledCall.handledAt).toISOString(),
        })
        return
      }

      addCallToQueue(data)
      syncStreamsIncomingCall(data)

      // Log current queue state
      const currentQueue = getCallQueue()
      console.log('📋 Queue after adding call:', {
        callId: data.callId,
        callHeaderId: data.callHeaderId,
        queueSize: currentQueue.length,
        queueCalls: currentQueue.map((c) => ({callId: c.callId, callHeaderId: c.callHeaderId})),
      })

      // Always try to process queue (processNextCall will check if modal is showing)
      // This ensures queued calls are processed when the current modal closes
      setTimeout(() => {
        if (processNextCallRef.current) {
          processNextCallRef.current()
        }
      }, 100)
    },
    [auth, currentUser, isAgentAvailable, isIncomingCallMaster]
  )
  showIncomingCallRef.current = showIncomingCall

  // Publish master-tab guard for foreground Firebase messages (non-React path).
  useEffect(() => {
    setIncomingCallMasterGuard(() => isIncomingCallMaster)
    return () => setIncomingCallMasterGuard(null)
  }, [isIncomingCallMaster])

  // Close modal and stop ringing if this tab loses master status.
  useEffect(() => {
    if (isIncomingCallMaster) return
    if (!showModalRef.current && !callDataRef.current) return
    hideIncomingCall()
  }, [isIncomingCallMaster, hideIncomingCall])

  // Dismiss an already-open incoming modal when CCP goes Offline (gate only blocks new ones).
  useEffect(() => {
    if (!isLocalConnectInitialized || localAgentStatus !== 'Offline') return
    if (!showModalRef.current && !callDataRef.current && !standbyCallRef.current) return

    const active = callDataRef.current
    callLogger.warn('🔷 Incoming call dismissed — CCP went Offline', {
      callId: active?.callId,
      callHeaderId: active?.callHeaderId,
      hadStandby: Boolean(standbyCallRef.current),
    })
    streamsStore.cancelAutoAccept()
    if (standbyCallRef.current) {
      resolveStandby('ENDED')
    }
    hideIncomingCall()
  }, [isLocalConnectInitialized, localAgentStatus, hideIncomingCall, resolveStandby])

  const removeActiveCall = useCallback(
    (callId: string) => {
      const guardrail =
        getCallGuardrail(callId) ?? getCallGuardrails().find((record) => record.callId === callId)
      // Unanswered pre-call questions pin the modal open (this one intentionally applies
      // mid-call too, hence no isClosed check). Exhausted submit attempts release it, so a
      // failing endpoint cannot leave the interpreter with a modal they can never dismiss.
      const isBlocked =
        guardrail &&
        guardrail.hasPreCallQuestions &&
        !guardrail.preCallSubmitted &&
        !hasExhaustedSubmitAttempts(guardrail)
      if (isBlocked) return

      setActiveCalls((prev) =>
        prev.filter((call) => call.callId !== callId && call.callHeaderId !== callId)
      )
      if (guardrail) {
        removeCallGuardrail(guardrail.callHeaderId)
        refreshCallGuardrails()
      }
    },
    [refreshCallGuardrails]
  )

  // Register Firebase incoming call handler (master tab only)
  useEffect(() => {
    if (!isIncomingCallMaster) {
      setIncomingCallHandler(() => {})
      return () => setIncomingCallHandler(() => {})
    }

    setIncomingCallHandler(showIncomingCall)
    return () => {
      setIncomingCallHandler(() => {})
    }
  }, [showIncomingCall, isIncomingCallMaster])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current)
      }
    }
  }, [])

  const reassertPresence = useCallback(async () => {
    const connection = connectionRef.current
    try {
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        await connection.invoke('Pong')
      }
    } catch (error) {
      console.error('Error confirming presence:', error)
    }
  }, [])

  const triggerOfflineWarning = useCallback(() => {
    setShowOfflineWarning(true)
    reassertPresence()
  }, [reassertPresence])

  const dismissOfflineWarning = useCallback(async () => {
    setShowOfflineWarning(false)
    await reassertPresence()
  }, [reassertPresence])

  const clearCallAcceptedByOther = useCallback(() => {
    setCallAcceptedByOther(false)
    isAcceptedAndConnectingRef.current = false
    setIsAcceptedAndConnecting(false)
    if (connectingTimeoutRef.current) {
      clearTimeout(connectingTimeoutRef.current)
      connectingTimeoutRef.current = null
    }
    if (callAcceptedByOtherTimeoutRef.current) {
      clearTimeout(callAcceptedByOtherTimeoutRef.current)
      callAcceptedByOtherTimeoutRef.current = null
    }
    if (softphoneRecoveryLockedRef.current || isSoftphoneRecoveryLocked()) {
      setShowSoftphoneRecoveryPanel(true)
    }
  }, [])

  const value: IncomingCallContextType = {
    showModal,
    callData,
    activeCalls,
    callAcceptedByOther,
    isAcceptedAndConnecting,
    isStandby,
    showCallAcceptedByOtherPopup,
    showOfflineWarning,
    hasDeviceToken,
    tokenPromptActive,
    setTokenPromptActive,
    showIncomingCall,
    hideIncomingCall,
    acceptCall,
    rejectCall,
    removeActiveCall,
    clearCallAcceptedByOther,
    triggerOfflineWarning,
    dismissOfflineWarning,
    callGuardrails,
    // Must use the same predicate as the incoming-call gates and the purge. This drives the
    // widget's force-offline watchdog, so if it disagreed it would drag the interpreter back
    // offline the moment the submit-attempt safety valve released them.
    hasPendingPreCallQuestions: callGuardrails.some(isBlockingGuardrail),
    registerCallGuardrail,
    updateCallGuardrailState,
    registerPreCallSubmitFailure,
    showPendingPreCallGuardrail,
    softphoneRecoveryLocked,
    showSoftphoneRecoveryPanel,
    clearSoftphoneRecoveryLock,
    armSoftphoneRecoveryLock,
  }

  return <IncomingCallContext.Provider value={value}>{children}</IncomingCallContext.Provider>
}

export const useIncomingCall = (): IncomingCallContextType => {
  return useContext(IncomingCallContext)
}
